import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useNotifications } from '/src/hooks';
import { isDevelopment, isE2E } from '/src/utils';

const VERSION_POLL_MS = 60_000;
const DEBUG_SERVER_VERSION_KEY = 'ce.shell.debug.serverVersion';
const DEBUG_POLL_MS_KEY = 'ce.shell.debug.versionPollMs';
const PENDING_RELOAD_VERSION_KEY = 'ce.shell.pendingReloadVersion';

function canUseDebugVersion() {
  return isDevelopment() || isE2E();
}

function getDebugServerVersion(search) {
  if (!canUseDebugVersion()) return null;

  const fromSession = window.sessionStorage.getItem(DEBUG_SERVER_VERSION_KEY);
  if (fromSession) return fromSession;

  const fromQuery = new URLSearchParams(search).get('shellServerVersion');
  if (fromQuery) return fromQuery;

  return null;
}

function getPollIntervalMs() {
  const fromWindow = Number(window.CE_DEBUG_VERSION_POLL_MS);
  if (Number.isFinite(fromWindow) && fromWindow >= 100) return fromWindow;

  if (!canUseDebugVersion()) return VERSION_POLL_MS;

  const fromSession = window.sessionStorage.getItem(DEBUG_POLL_MS_KEY);
  if (!fromSession) return VERSION_POLL_MS;

  const parsed = Number(fromSession);
  if (!Number.isFinite(parsed) || parsed < 100) return VERSION_POLL_MS;

  return parsed;
}

function getPendingReloadVersion() {
  try {
    return window.sessionStorage.getItem(PENDING_RELOAD_VERSION_KEY);
  } catch {
    return null;
  }
}

function acknowledgeVersion(version) {
  try {
    window.sessionStorage.setItem(PENDING_RELOAD_VERSION_KEY, version);
  } catch {
    console.error('[VERSION] failed to persist pending reload version');
  }
}

function clearPendingReloadVersion() {
  try {
    window.sessionStorage.removeItem(PENDING_RELOAD_VERSION_KEY);
  } catch {
    console.error('[VERSION] failed to clear pending reload version');
  }
}

function hardReloadWithCacheBuster(href) {
  const nextUrl = new URL(href, window.location.origin);
  nextUrl.searchParams.set('ce_reload_ts', String(Date.now()));
  window.location.replace(nextUrl.toString());
}

export function OutdatedBundleHandler() {
  const notifications = useNotifications();
  const { pathname, search, hash } = useLocation();
  const href = `${pathname}${search}${hash}`;

  useEffect(() => {
    // when mounted initially, get the first version immediately, and store it in the window.
    const fetchVersion = async () => {
      try {
        const debugVersion = getDebugServerVersion(search);
        if (debugVersion) return { version: debugVersion };

        const request = await fetch(`/v.json?ts=${Date.now()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'no-store',
        });
        const data = await request.json();
        if (!window.version) {
          window.version = data.version;
        }
        return data;
      } catch {
        console.error('[VERSION] fetch failed');
      }
    };

    const checkVersionAndNotify = (newData) => {
      const pendingReloadVersion = getPendingReloadVersion();

      if (newData?.version === window.version && pendingReloadVersion) {
        clearPendingReloadVersion();
      }

      if (newData && newData.version !== window.version && newData.version !== pendingReloadVersion) {
        const serverVersion = newData.version;

        // the version has changed, notify the user to reload the page to get the latest changes.
        notifications.info({
          id: 'new_app_version',
          title: 'New version available',
          onDismiss: () => {
            acknowledgeVersion(serverVersion);
          },
          content: (
            <span className="flex items-center">
              Please
              <button
                onClick={() => {
                  acknowledgeVersion(serverVersion);
                  hardReloadWithCacheBuster(href);
                }}
                className="text-link mx-1 !text-white !underline"
              >
                reload the page
              </button>
              to get the latest changes.
            </span>
          ),
        });
      }
    };

    // then set up the interval to check for new versions every minute

    const intervalId = window.setInterval(async () => {
      const newData = await fetchVersion();
      checkVersionAndNotify(newData);
    }, getPollIntervalMs());

    fetchVersion().then(checkVersionAndNotify);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [notifications, search, href]);

  return null;
}
